import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, CheckCircle, Calendar } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { formatCurrencyAmount } from '../../utils/currency';
import { Service } from '../../types/service.types';

interface ServiceDetailModalProps {
  service: Service;
  isOpen: boolean;
  onClose: () => void;
}

const BENEFITS = [
  'Professional certified therapists',
  'Flexible scheduling',
  'Clean, comfortable facilities',
  'Customized to your needs',
];

export const ServiceDetailModal: FC<ServiceDetailModalProps> = ({ service, isOpen, onClose }) => {
  const navigate = useNavigate();

  const handleBookAppointment = () => {
    onClose();
    navigate(`/book?service=${service.slug}`);
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${remainingMinutes} min`;
  };

  const getCategoryVariant = (category: string) => {
    const variants: Record<string, 'default' | 'success' | 'warning' | 'info'> = {
      Relaxation: 'success',
      Therapeutic: 'info',
      Sports: 'warning',
      Specialized: 'default',
    };
    return variants[category] || 'default';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" className="max-h-[90vh] overflow-y-auto">
      <div className="space-y-6">
        {/* Service Image */}
        <div className="relative aspect-video w-full overflow-hidden rounded-lg">
          {service.imageUrl ? (
            <img src={service.imageUrl} alt={service.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-slate-800">
              <span className="text-gray-400 dark:text-slate-500">No image available</span>
            </div>
          )}
          <div className="absolute left-4 top-4">
            <Badge variant={getCategoryVariant(service.category)} size="md">
              {service.category}
            </Badge>
          </div>
        </div>

        {/* Service Header */}
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">{service.name}</h2>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" aria-hidden="true" />
              <span>{formatDuration(service.duration)}</span>
            </div>
            {service.availableTherapistCount !== undefined && (
              <div className="flex items-center gap-1.5">
                <Users className="h-4 w-4" aria-hidden="true" />
                <span>
                  {service.availableTherapistCount}{' '}
                  {service.availableTherapistCount === 1 ? 'therapist' : 'therapists'} available
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">About This Service</h3>
          <p className="leading-relaxed text-gray-600 dark:text-slate-400">{service.description}</p>
        </div>

        {/* What's Included */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">What's Included</h3>
          <ul className="space-y-2">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-500 dark:text-green-400" aria-hidden="true" />
                <span className="text-gray-700 dark:text-slate-300">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Price and CTA */}
        <div className="flex flex-col items-center gap-4 border-t border-gray-200 dark:border-slate-700 pt-6 sm:flex-row sm:justify-between">
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-500 dark:text-slate-400">Starting from</p>
            <p className="text-3xl font-bold text-primary-600">{formatCurrencyAmount(service.basePrice)}</p>
          </div>
          <Button
            variant="primary"
            size="lg"
            leftIcon={<Calendar className="h-5 w-5" />}
            onClick={handleBookAppointment}
            className="w-full sm:w-auto"
          >
            Book Appointment
          </Button>
        </div>
      </div>
    </Modal>
  );
};
